import React from 'react';

import TabsNavigation from '#/components/basic/Button/TabsNavigation';
import Dropdown from '#/components/basic/inputs/Dropdown';
import { useSelector } from '#/reduxState/store';

import { useIsMobileBuySellRoute } from '../dashboardDetect';

import { PanelState, WrappedUserOrderType } from './buyPanelReducer';

type Props = {
  balanceBase?: string;
  balanceQuote?: string;

  limitOnly: boolean;
  /** Hover tip explaining why market orders are not available */
  limitOnlyReason?: string;
  /**
   * Disables Stop Market separately from {@link limitOnly} — stop market
   * orders stay available on closed intermittent markets (they trigger after
   * reopen) but not on post-only (limitMakerOnly) markets
   */
  stopMarketDisabled: boolean;
  triggerEnabled: boolean;

  acceptingInput: boolean;

  selectedType: PanelState['orderType'];
  onChangeType: (type: WrappedUserOrderType) => void;
};

// TODO: rename to OrderTypeSelector
function PanelHeader({
  acceptingInput,
  selectedType,
  limitOnly,
  limitOnlyReason,
  stopMarketDisabled,
  triggerEnabled,
  onChangeType,
}: Props) {
  // when restricted region is defined it means the user is not allowed to trade
  const isRestrictedRegion = !!useSelector(
    state => state.region.restrictedRegion
  );
  const isMobileBuySellRoute = useIsMobileBuySellRoute();

  return isMobileBuySellRoute ? (
    <Dropdown<WrappedUserOrderType>
      disabled={isRestrictedRegion || !acceptingInput}
      options={[
        { label: 'Limit', value: 'limit' },
        { label: 'Market', value: 'market', isDisabled: limitOnly },
        { label: 'Stop Limit', value: 'stopLimit' },
        {
          label: 'Stop Market',
          value: 'stopMarket',
          isDisabled: stopMarketDisabled,
        },
      ]}
      selected={
        triggerEnabled
          ? selectedType === 'limit'
            ? 'stopLimit'
            : 'stopMarket'
          : selectedType
      }
      onChange={onChangeType}
    />
  ) : (
    <>
      <TabsNavigation
        padding="8px 0 0 0"
        buttons={[
          {
            title: 'Limit',
            active: selectedType === 'limit' && !triggerEnabled,
            onClick: () => onChangeType('limit'),
            stretch: true,
          },
          {
            title: 'Market',
            active: selectedType === 'market' && !triggerEnabled,
            onClick: () => onChangeType('market'),
            stretch: true,
            disabled: limitOnly,
            tooltip: limitOnly ? limitOnlyReason : undefined,
          },
        ]}
        lastButton={{
          active: triggerEnabled,
          element: (
            <Dropdown<WrappedUserOrderType>
              disabled={isRestrictedRegion || !acceptingInput}
              options={[
                { label: 'Stop Limit', value: 'stopLimit' },
                {
                  label: 'Stop Market',
                  value: 'stopMarket',
                  isDisabled: stopMarketDisabled,
                },
              ]}
              singleValueColor={
                triggerEnabled ? 'var(--white)' : 'var(--gray4c)'
              }
              noBorder
              disabledSearchOnDesktop
              initialText="Stop Limit"
              selected={
                !triggerEnabled
                  ? undefined
                  : selectedType === 'limit'
                  ? 'stopLimit'
                  : 'stopMarket'
              }
              onChange={onChangeType}
            />
          ),
        }}
      />
    </>
  );
}

export default PanelHeader;
